import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VetrinaPage from "./pages/VetrinaPage";
import LavoraConNoiPage from "./pages/LavoraConNoiPage";
import WaitingRoomPage from "./pages/WaitingRoomPage";
import DashboardLayout from "./components/DashboardLayout";
import DashboardHome from "./pages/DashboardHome";
import CatalogPage from "./pages/CatalogPage";
import VehicleDetailPage from "./pages/VehicleDetailPage";
import OffersPage from "./pages/OffersPage";
import OfferteAttivePage from "./pages/OfferteAttivePage";
import ContrattiClientePage from "./pages/ContrattiClientePage";
import OrdersPage from "./pages/OrdersPage";
import ContactsPage from "./pages/ContactsPage";
import PortafoglioClientiPage from "./pages/PortafoglioClientiPage";
import GestionePratichePage from "./pages/GestionePratichePage";
import UsersPage from "./pages/UsersPage";
import UploadPage from "./pages/UploadPage";
import ExportPage from "./pages/ExportPage";
import ModuliUtiliPage from "./pages/ModuliUtiliPage";
import OfferteDelMesePage from "./pages/OfferteDelMesePage";
import OffertaMeseDetailPage from "./pages/OffertaMeseDetailPage";
import RepartiPage from "./pages/RepartiPage";
import MioRepartoPage from "./pages/MioRepartoPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import ParametriCalcoloPage from "./pages/ParametriCalcoloPage";
import ConvenzioniAdminPage from "./pages/ConvenzioniAdminPage";
import ConvenzioneOverviewPage from "./pages/ConvenzioneOverviewPage";
import ConvenzioniModule from "./pages/ConvenzioniModule";
import ClienteContattiUtiliPage from "./pages/ClienteContattiUtiliPage";
import ClienteProfiloPage from "./pages/ClienteProfiloPage";
import AdminOfferteInLavorazionePage from "./pages/AdminOfferteInLavorazionePage";
import ClienteCatalogoPage from "./pages/ClienteCatalogoPage";
import ConvenzioniAssegnatePage from "./pages/ConvenzioniAssegnatePage";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles, blockCliente }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Se l'utente è in stato "pending" (Sala d'Attesa), redirect alla Waiting Room
  if (user.status === 'pending') {
    return <Navigate to="/waiting-room" replace />;
  }
  
  // BLOCCO CLIENTE: Se blockCliente=true e l'utente è cliente (smart o convenzione), redirect a offerte
  if (blockCliente && ['cliente_smart', 'cliente'].includes(user.role)) {
    return <Navigate to="/dashboard/offerte" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Route specifica per la Waiting Room (solo per utenti pending)
const WaitingRoomRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Se l'utente è attivo, redirect alla dashboard normale
  if (user.status !== 'pending') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (user) {
    // Se l'utente è pending, vai alla waiting room
    if (user.status === 'pending') {
      return <Navigate to="/waiting-room" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Vetrina - No Auth Required */}
      <Route path="/vetrina" element={<VetrinaPage />} />
      
      {/* Lavora con Noi - No Auth Required */}
      <Route path="/lavora-con-noi" element={<LavoraConNoiPage />} />
      
      {/* Waiting Room - Solo per utenti pending */}
      <Route path="/waiting-room" element={
        <WaitingRoomRoute>
          <WaitingRoomPage />
        </WaitingRoomRoute>
      } />
      
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Password Dimenticata */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        {/* Dashboard Home - redirect clienti a offerte */}
        <Route index element={
          <ProtectedRoute blockCliente>
            <DashboardHome />
          </ProtectedRoute>
        } />
        {/* Catalogo - BLOCCATO per clienti */}
        <Route path="catalogo" element={
          <ProtectedRoute blockCliente>
            <CatalogPage />
          </ProtectedRoute>
        } />
        {/* Dettaglio Veicolo - BLOCCATO per clienti */}
        <Route path="veicolo/:id" element={
          <ProtectedRoute blockCliente>
            <VehicleDetailPage />
          </ProtectedRoute>
        } />
        {/* Le Mie Offerte - accessibile ai clienti */}
        <Route path="offerte" element={<OffersPage />} />
        {/* Fase 2.6 — Catalogo Offerte Cliente (landing post-login se 0 preventivi) */}
        <Route path="catalogo-offerte" element={
          <ProtectedRoute allowedRoles={["cliente", "cliente_smart"]}>
            <ClienteCatalogoPage />
          </ProtectedRoute>
        } />
        {/* I Miei Contratti - SOLO per clienti con contratti */}
        <Route path="contratti" element={<ContrattiClientePage />} />
        {/* Fase 1.4 Punto 4 - Portale Cliente */}
        <Route path="contatti-utili" element={<ClienteContattiUtiliPage />} />
        <Route path="profilo" element={<ClienteProfiloPage />} />
        {/* Offerte Attive - BLOCCATO per clienti */}
        <Route path="offerte-attive" element={
          <ProtectedRoute blockCliente>
            <OfferteAttivePage />
          </ProtectedRoute>
        } />
        {/* Ordini - BLOCCATO per clienti */}
        <Route path="ordini" element={
          <ProtectedRoute blockCliente>
            <OrdersPage />
          </ProtectedRoute>
        } />
        {/* Rubrica - BLOCCATO per clienti */}
        <Route path="rubrica" element={
          <ProtectedRoute blockCliente>
            <ContactsPage />
          </ProtectedRoute>
        } />
        {/* Portafoglio - BLOCCATO per clienti */}
        <Route path="portafoglio" element={
          <ProtectedRoute blockCliente>
            <PortafoglioClientiPage />
          </ProtectedRoute>
        } />
        <Route path="pratiche" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <GestionePratichePage />
          </ProtectedRoute>
        } />
        <Route path="utenti" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "top_agent"]}>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="upload" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
            <UploadPage />
          </ProtectedRoute>
        } />
        <Route path="export" element={
          <ProtectedRoute allowedRoles={["admin", "super_admin", "top_agent"]}>
            <ExportPage />
          </ProtectedRoute>
        } />
        {/* Gestione Reparti - Solo Super Admin */}
        <Route path="reparti" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <RepartiPage />
          </ProtectedRoute>
        } />
        {/* Il Mio Reparto - Per Top Agent */}
        <Route path="mio-reparto" element={
          <ProtectedRoute allowedRoles={["top_agent", "admin", "super_admin"]}>
            <MioRepartoPage />
          </ProtectedRoute>
        } />
        {/* Impostazioni Sede Centrale - Solo Super Admin */}
        <Route path="impostazioni" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <AdminSettingsPage />
          </ProtectedRoute>
        } />
        {/* Parametri di Calcolo - Solo Super Admin */}
        <Route path="parametri-calcolo" element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <ParametriCalcoloPage />
          </ProtectedRoute>
        } />
        {/* Gestione Convenzioni - Super Admin + Admin di Sede + Top Agent referenti */}
        <Route path="convenzioni" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "top_agent"]}>
            <ConvenzioniAdminPage />
          </ProtectedRoute>
        } />
        {/* 2.6.4 — Convenzioni Assegnate (Top Agent referenti + Admin + Agente referente) */}
        <Route path="convenzioni-assegnate" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "top_agent", "agente"]}>
            <ConvenzioniAssegnatePage />
          </ProtectedRoute>
        } />
        <Route path="convenzioni/:convenzioneId/overview" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "top_agent"]}>
            <ConvenzioneOverviewPage />
          </ProtectedRoute>
        } />
        {/* Fase 2 Sub-release 2.4b — Offerte in Lavorazione (Admin/Agent/TopAgent) */}
        <Route path="offerte-in-lavorazione" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "top_agent", "agente"]}>
            <AdminOfferteInLavorazionePage />
          </ProtectedRoute>
        } />
        {/* Moduli Utili - accessibile a Admin e Agenti */}
        <Route path="moduli" element={
          <ProtectedRoute blockCliente>
            <ModuliUtiliPage />
          </ProtectedRoute>
        } />
        {/* Offerte del Mese */}
        <Route path="offerte-mese" element={
          <ProtectedRoute blockCliente>
            <OfferteDelMesePage />
          </ProtectedRoute>
        } />
        <Route path="offerta-mese/:id" element={
          <ProtectedRoute blockCliente>
            <OffertaMeseDetailPage />
          </ProtectedRoute>
        } />
      </Route>
      
      {/* Modulo Convenzioni Aziendali - flusso cliente indipendente */}
      <Route path="/convenzioni/*" element={<ConvenzioniModule />} />
      
      {/* Home page pubblica = Vetrina */}
      <Route path="/" element={<Navigate to="/vetrina" replace />} />
      <Route path="*" element={<Navigate to="/vetrina" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
